// src/components/AchievementsManage.js
import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import {
  IoTrophyOutline, IoBarbellOutline, IoCalendarOutline, IoTrendingUpOutline,
  IoRestaurantOutline, IoFlameOutline, IoStarOutline, IoShieldOutline,
  IoAddOutline, IoCreateOutline, IoTrashOutline, IoCloseOutline,
} from 'react-icons/io5';

const ICONS = {
  dumbbell:  IoBarbellOutline,
  calendar:  IoCalendarOutline,
  chart:     IoTrendingUpOutline,
  nutrition: IoRestaurantOutline,
  trophy:    IoTrophyOutline,
  barbell:   IoBarbellOutline,
  star:      IoStarOutline,
  flame:     IoFlameOutline,
  shield:    IoShieldOutline,
};

const ICON_OPTIONS = ['dumbbell', 'calendar', 'chart', 'nutrition', 'trophy', 'barbell', 'star', 'flame', 'shield'];

const CONDITION_TYPES = [
  { value: '',                    label: 'Sin condición automática (manual)' },
  { value: 'workout_count',       label: 'Nº de entrenamientos' },
  { value: 'progress_count',      label: 'Nº de mediciones de progreso' },
  { value: 'nutrition_assigned',  label: 'Tiene plan de nutrición asignado' },
  { value: 'program_assigned',    label: 'Tiene programa asignado' },
  { value: 'manual',              label: 'Otorgado manualmente por el coach' },
];

const inp = 'w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-marizan-red transition-colors text-sm';
const lbl = 'block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5';
const sel = 'w-full bg-[#0f1624] border border-white/10 rounded-xl px-4 py-2.5 text-gray-100 focus:outline-none focus:border-marizan-red transition-colors text-sm';

const emptyForm = { name: '', description: '', icon: 'trophy', condition_type: '', condition_value: '' };

const RewardModal = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial || emptyForm);
  const [saving, setSaving] = useState(false);

  const needsValue = form.condition_type === 'workout_count' || form.condition_type === 'progress_count';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      icon: form.icon || 'trophy',
      condition_type: form.condition_type || 'manual',
      condition_value: needsValue && form.condition_value !== '' ? parseInt(form.condition_value) : null,
    };
    try {
      let res;
      if (initial?.id) {
        res = await api.put(`/admin/rewards/${initial.id}`, payload);
        toast.success('Logro actualizado');
      } else {
        res = await api.post('/admin/rewards', payload);
        toast.success('Logro creado');
      }
      onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="glass-panel rounded-2xl w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">{initial?.id ? 'Editar logro' : 'Nuevo logro'}</h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors">
            <IoCloseOutline size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={lbl}>Nombre *</label>
            <input className={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="ej. Récord Personal" required />
          </div>
          <div>
            <label className={lbl}>Descripción</label>
            <textarea className={inp + ' resize-none'} rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Describe qué representa este logro..." />
          </div>
          <div>
            <label className={lbl}>Icono</label>
            <div className="flex flex-wrap gap-2">
              {ICON_OPTIONS.map((ico) => {
                const Icon = ICONS[ico] || IoTrophyOutline;
                return (
                  <button key={ico} type="button" onClick={() => setForm({ ...form, icon: ico })}
                    className={`p-2.5 rounded-xl border transition-colors ${form.icon === ico ? 'border-marizan-red bg-marizan-red/20 text-white' : 'border-white/10 text-gray-400 hover:text-white hover:border-white/30'}`}>
                    <Icon size={20} />
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Condición automática</label>
              <select
                className={sel + ' dark-select cursor-pointer'}
                value={form.condition_type}
                onChange={e => setForm({ ...form, condition_type: e.target.value, condition_value: '' })}
              >
                {CONDITION_TYPES.map(c => (
                  <option key={c.value} value={c.value} className="bg-[#0f1624] text-gray-100">
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            {needsValue && (
              <div>
                <label className={lbl}>Cantidad requerida</label>
                <input type="number" min="1" className={inp} value={form.condition_value} onChange={e => setForm({ ...form, condition_value: e.target.value })} placeholder="ej. 10" />
              </div>
            )}
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 bg-white/5 border border-white/10 hover:bg-white/10 text-gray-300 font-semibold py-2.5 rounded-xl transition-colors">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="flex-1 bg-marizan-red hover:bg-red-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-xl transition-colors">
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AchievementsManage = () => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | reward object

  useEffect(() => {
    api.get('/admin/rewards')
      .then(r => setRewards(r.data || []))
      .catch(() => toast.error('Error al cargar logros'))
      .finally(() => setLoading(false));
  }, []);

  const handleSaved = (saved) => {
    setRewards(prev => {
      const idx = prev.findIndex(r => r.id === saved.id);
      if (idx >= 0) { const n = [...prev]; n[idx] = saved; return n; }
      return [...prev, saved];
    });
    setModal(null);
  };

  const handleDelete = async (reward) => {
    if (!reward.coach_id) { toast.error('No puedes eliminar logros del sistema'); return; }
    if (!window.confirm(`¿Eliminar "${reward.name}"?`)) return;
    try {
      await api.delete(`/admin/rewards/${reward.id}`);
      setRewards(prev => prev.filter(r => r.id !== reward.id));
      toast.success('Logro eliminado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  if (loading) return (
    <div className="w-full container mx-auto p-4 md:p-8 text-white animate-fade-up">
      <div className="skeleton h-10 w-64 rounded mb-3" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-28 rounded-xl" />)}
      </div>
    </div>
  );

  const systemRewards = rewards.filter(r => !r.coach_id);
  const customRewards = rewards.filter(r => r.coach_id);

  const RewardCard = ({ reward }) => {
    const Icon = ICONS[reward.icon] || IoTrophyOutline;
    const isCustom = !!reward.coach_id;
    return (
      <div className="glass-panel rounded-xl p-4 flex items-start gap-4 hover:border-white/20 transition-colors">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isCustom ? 'bg-marizan-red/20 border border-marizan-red/30' : 'bg-yellow-600/20 border border-yellow-600/30'}`}>
          <Icon size={20} className={isCustom ? 'text-marizan-red' : 'text-yellow-400'} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-bold text-white text-sm">{reward.name}</p>
            {isCustom && <span className="text-xs px-1.5 py-0.5 bg-marizan-red/20 text-marizan-red rounded-full">Personalizado</span>}
          </div>
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{reward.description}</p>
          {reward.condition_type && (
            <p className="text-xs text-gray-600 mt-1 italic">
              {reward.condition_type === 'workout_count'      && `Auto: ${reward.condition_value} entrenos`}
              {reward.condition_type === 'progress_count'     && `Auto: ${reward.condition_value} mediciones`}
              {reward.condition_type === 'nutrition_assigned' && 'Auto: plan de nutrición asignado'}
              {reward.condition_type === 'program_assigned'   && 'Auto: programa asignado'}
              {reward.condition_type === 'manual'             && 'Manual (otorgado por coach)'}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button onClick={() => setModal(reward)} className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            <IoCreateOutline size={16} />
          </button>
          {isCustom && (
            <button onClick={() => handleDelete(reward)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
              <IoTrashOutline size={16} />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full container mx-auto p-4 md:p-8 text-white animate-fade-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <IoTrophyOutline size={26} className="text-yellow-400" />
            <h1 className="text-2xl md:text-3xl font-bold">Gestión de Logros</h1>
          </div>
          <p className="text-gray-400 text-sm">{rewards.length} logros en el catálogo ({customRewards.length} personalizados)</p>
        </div>
        <button onClick={() => setModal('create')} className="flex items-center gap-2 bg-marizan-red hover:bg-red-700 text-white font-bold px-4 py-2.5 rounded-xl transition-colors text-sm">
          <IoAddOutline size={18} /> Nuevo logro
        </button>
      </div>

      {customRewards.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Personalizados por ti</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {customRewards.map(r => <RewardCard key={r.id} reward={r} />)}
          </div>
        </div>
      )}

      <div>
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Logros del sistema</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {systemRewards.map(r => <RewardCard key={r.id} reward={r} />)}
        </div>
      </div>

      {modal && (
        <RewardModal
          initial={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default AchievementsManage;
